import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const DEFAULTS = { tax_rate: 10, tax_name: 'GST', currency_symbol: '$' };
let _cache = null;
let _inflight = null;

/**
 * Tenant finance settings (currency symbol, tax rate, tax label).
 * Reads GET /tenant/tax-settings once and caches for the session.
 */
export const useTaxSettings = () => {
  const { api, user } = useAuth();
  const [settings, setSettings] = useState(_cache || DEFAULTS);
  const [loaded, setLoaded] = useState(!!_cache);

  useEffect(() => {
    if (_cache) { setSettings(_cache); setLoaded(true); return; }
    if (!user?.client_id || !api) return;
    if (!_inflight) {
      _inflight = api.get('/tenant/tax-settings')
        .then((res) => {
          _cache = { ...DEFAULTS, ...(res.data || {}) };
          return _cache;
        })
        .catch(() => { _cache = DEFAULTS; return _cache; });
    }
    let active = true;
    _inflight.then((s) => { if (active) { setSettings(s); setLoaded(true); } });
    return () => { active = false; };
  }, [api, user?.client_id]);

  const currencySymbol = settings.currency_symbol || '$';
  const formatCurrency = (value, { decimals = 2 } = {}) =>
    `${currencySymbol}${new Intl.NumberFormat(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value || 0)}`;

  return {
    currencySymbol,
    taxRate: settings.tax_rate ?? 10,
    taxName: settings.tax_name || 'GST',
    formatCurrency,
    loaded,
  };
};

/** Reset the module cache (e.g. after settings change). */
export const resetTaxSettingsCache = () => { _cache = null; _inflight = null; };

export default useTaxSettings;
